import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page, PageHeader, type PageCrumb, type PageTemplate, type PageWidth } from '@beyond-js/ui/react';
import { usePresence } from '@accounts/session';
import { Origin } from './origin';

/** Navigation state that asks the next screen to move focus to its heading */
export const moved = { moved: true };

/** Whether a click is a plain primary one, which the application follows itself */
export function plain(event: MouseEvent | globalThis.MouseEvent): boolean {
	return !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * One signed-in screen in the family page system (D52): the shared `Page` beside the sidebar, edge to
 * edge with its content one gutter after the navigation, the arrival line first when a product sent the
 * person here (D54), and the one `PageHeader` with the screen's H1, a facts line of the object's own
 * properties (D160), its lead sentence under them at the reading measure (D191) and the title
 * line's actions. `width` is the main column's tier: `fluid` for collections, `standard` for a main
 * column, `form` for inputs and `reading` for prose.
 *
 * A screen reached from the sidebar, the palette, the bell or the profile menu moves focus to its
 * heading; an address that names one of its regions (`#factors`) moves focus to that region once it is
 * drawn. The breadcrumb's links are followed in the application, as every other link of this interface is.
 */
export function Screen({
	title,
	facts,
	lead,
	actions,
	crumbs,
	template = 'detail',
	width = 'standard',
	children
}: {
	title?: string;
	facts?: string;
	lead?: string;
	actions?: ReactNode;
	crumbs?: PageCrumb[];
	template?: PageTemplate;
	width?: PageWidth;
	children?: ReactNode;
}) {
	const { language } = usePresence();
	const location = useLocation();
	const navigate = useNavigate();
	const heading = useRef<HTMLHeadingElement>(null);

	useEffect(() => {
		// An address that names a region (`/security#factors`) lands on it once it is drawn, within a bound
		const id = decodeURIComponent(location.hash.slice(1));
		if (!id) {
			if ((location.state as { moved?: boolean } | null)?.moved) heading.current?.focus({ preventScroll: true });
			return;
		}
		let tries = 0;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const land = () => {
			const region = globalThis.document?.getElementById(id);
			if (region) {
				region.scrollIntoView?.({ block: 'start' });
				// The opening may replay this focus after earlier regions finish drawing; keep the destination visible then.
				region.focus();
			} else if (++tries < 40) timer = setTimeout(land, 50);
		};
		land();
		return () => clearTimeout(timer);
	}, [location.key, location.state, location.hash]);

	// The breadcrumb's plain clicks are followed in the application rather than by loading the page again
	useEffect(() => {
		const header = heading.current?.closest<HTMLElement>('.bui-page-header');
		if (!header) return;
		const follow = (event: globalThis.MouseEvent) => {
			const link = (event.target as Element).closest?.('.bui-crumbs a');
			if (!(link instanceof HTMLAnchorElement) || !plain(event)) return;
			const url = new URL(link.href);
			if (url.origin !== globalThis.location.origin) return;
			event.preventDefault();
			navigate(`${url.pathname}${url.search}`, { state: moved });
		};
		header.addEventListener('click', follow);
		return () => header.removeEventListener('click', follow);
	}, [navigate, title]);

	const header = title ? (
		<PageHeader title={title} crumbs={crumbs} facts={facts} lead={lead} actions={actions} headingRef={heading} labels={PageHeader.labels[language]} />
	) : null;

	return (
		<Page template={template} width={width} arrival={<Origin />} header={header}>
			{children}
		</Page>
	);
}
